import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { HandHeart, Building2, ArrowRight, ExternalLink, Mail, Phone } from 'lucide-react';
import { useStore } from '../data/store';

const SponsorCard = ({ sponsor }: { sponsor: any }) => {
  return (
    <div className="group bg-[#222222] rounded-lg overflow-hidden border border-white/10 hover:border-cardinal/30 transition-all hover:shadow-lg hover:shadow-cardinal/5">
      <div className="relative bg-gradient-to-br from-[#1A1A1A] to-[#222222] p-8 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="relative z-10 w-full h-48 flex items-center justify-center">
          <img
            src={sponsor.logo}
            alt={`${sponsor.name} logo`}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      </div>
      <div className="p-8">
        <h3 className="text-xl font-bold text-white mb-4">{sponsor.name}</h3>
        <div className="text-gray-400 mb-6 text-base leading-relaxed">
          {sponsor.description.split('\n').map((paragraph: string, index: number) => (
            <p key={index} className="mb-3 last:mb-0">{paragraph.trim()}</p>
          ))}
        </div>
        <a
          href={sponsor.website}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-cardinal hover:text-cardinal-hover transition-colors group-hover:translate-x-1 transition-transform text-base"
        >
          Visit Website
          <ExternalLink className="w-5 h-5 ml-2" />
        </a>
      </div>
    </div>
  );
};

const Sponsors = () => {
  const navigate = useNavigate();
  const { sponsors } = useStore();
  
  // Filter active sponsors and sort by tier and order
  const activeSponsors = sponsors.filter(sponsor => sponsor.active);
  const premiumSponsors = activeSponsors
    .filter(sponsor => sponsor.tier === 'gold')
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const standardSponsors = activeSponsors
    .filter(sponsor => sponsor.tier === 'silver')
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="min-h-screen flex flex-col bg-[#111111]">
      <Navbar />
      
      <main className="flex-1 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-center mb-16">
            <div className="flex items-center gap-4 mb-4 justify-center">
              <HandHeart className="w-8 h-8 text-cardinal" />
              <h1 className="text-4xl font-bold text-white">Our Partners</h1>
            </div>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              These amazing organizations support Lincoln County baseball and help make our program possible.
              Join them in growing the future of baseball in our community.
            </p>
          </div>

          {/* All Partners Grid */}
          {activeSponsors.length > 0 && (
            <div className="mb-16 md:mb-24 space-y-16">
              {/* Premium Partners */}
              {premiumSponsors.length > 0 && (
                <div className="space-y-8">
                  <div className="space-y-8">
                    {premiumSponsors.map((sponsor) => (
                      <SponsorCard key={sponsor.id} sponsor={sponsor} />
                    ))}
                  </div>
                </div>
              )}

              {/* Standard Partners */}
              {standardSponsors.length > 0 && (
                <div className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {standardSponsors.map((sponsor) => (
                      <SponsorCard key={sponsor.id} sponsor={sponsor} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact Section */}
          <div className="bg-[#222222] rounded-lg p-6 md:p-12 text-center max-w-3xl mx-auto">
            <Building2 className="w-12 h-12 md:w-16 md:h-16 text-cardinal mx-auto mb-4 md:mb-6" />
            <h2 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4">Become a Partner Today</h2>
            <p className="text-gray-400 mb-6 md:mb-8 text-base md:text-lg leading-relaxed">
              Join our community of partners and help support the future of baseball in Lincoln County. 
              Our partnership coordinator will work with you to create a custom package that meets your needs.
            </p>
            <div className="space-y-4 md:space-y-6">
              <button
                onClick={() => navigate('/contact')}
                className="inline-flex items-center px-6 md:px-8 py-3 md:py-4 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg text-base md:text-lg font-semibold transition-all hover:scale-105 hover:shadow-glow"
              >
                Contact Us About Partnership
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6 text-gray-400">
                <a 
                  href="mailto:gobirds@lincolncountycardinals.com"
                  className="flex items-center gap-2 hover:text-white transition-colors text-sm md:text-base"
                >
                  <Mail className="w-4 h-4 md:w-5 md:h-5" />
                  gobirds@lincolncountycardinals.com
                </a>
                <a 
                  href="tel:+12077513271"
                  className="flex items-center gap-2 hover:text-white transition-colors text-sm md:text-base"
                >
                  <Phone className="w-4 h-4 md:w-5 md:h-5" />
                  (207) 751-3271
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Sponsors;